import axios, { type AxiosRequestConfig } from 'axios'

import { ROLE_KEY, TOKEN_KEY } from '@/utils/storage'
import { generateTraceId } from '@/utils/trace'

/**
 * 统一 axios 实例（前端方案 §三 / 技术开发文档 §4）：
 * - 请求拦截：注入 JWT（Authorization: Bearer）与 X-Trace-Id（跨服务链路排查）；
 * - 响应拦截：会话失效（401）集中清理并回登录页；
 * - 经 Vite dev server 代理 /api → localhost:8080，前端不配置跨域。
 */
const client = axios.create({
  timeout: 15000,
})

client.interceptors.request.use((config) => {
  // mock 模式（pnpm dev:mock）无真实登录会话：注入占位 token 以通过契约 security 校验（Prism 仅校验格式）
  const token =
    localStorage.getItem(TOKEN_KEY) ?? (import.meta.env.MODE === 'mock' ? 'mock-token' : null)
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  config.headers['X-Trace-Id'] = generateTraceId()
  return config
})

/** 若依响应协议：HTTP 恒 200，业务码在 body.code（未认证 code=401，业务失败 code!=200） */
export interface BizResponse<T = unknown> {
  code: number
  msg: string
  data?: T
}

/** 业务失败（code != 200）：msg 为后端友好文案，可直接展示给用户 */
export class BizError extends Error {
  readonly code: number

  constructor(code: number, msg: string) {
    super(msg)
    this.name = 'BizError'
    this.code = code
  }
}

/**
 * 会话失效统一处理：清本地会话 + 回登录页。
 * 用 window.location 硬跳转——client 处于 api 底层，import router 会与
 * store/守卫形成循环依赖；整页刷新同时保证 Pinia 内存态与本地存储一致。
 * 已无 token（如登录失败路径）或已在登录页时不处理，避免循环重定向。
 */
function handleUnauthorized() {
  if (!localStorage.getItem(TOKEN_KEY)) return
  localStorage.removeItem(TOKEN_KEY)
  localStorage.removeItem(ROLE_KEY)
  if (window.location.pathname !== '/login') {
    const redirect = encodeURIComponent(window.location.pathname + window.location.search)
    window.location.href = `/login?redirect=${redirect}`
  }
}

client.interceptors.response.use(
  (response) => {
    const body = response.data as BizResponse | undefined
    if (body && typeof body.code === 'number' && body.code === 401) {
      handleUnauthorized()
    }
    return response
  },
  (error: unknown) => {
    // 若依未认证响应走 HTTP 200 + body.code；此处兜底处理标准 HTTP 401（如网关/反代拦截）
    if (axios.isAxiosError(error) && error.response?.status === 401) {
      handleUnauthorized()
    }
    return Promise.reject(error)
  },
)

/** 业务请求入口：T 为 body.data 业务类型；code != 200 抛 BizError（页面 catch 后用 message 展示） */
export async function requestJson<T>(config: AxiosRequestConfig): Promise<T> {
  const response = await client.request<BizResponse<T>>(config)
  const body = response.data
  if (body.code !== 200) {
    throw new BizError(body.code, body.msg || '请求失败，请稍后重试')
  }
  return body.data as T
}

/** 业务请求入口：返回完整响应体（契约中业务字段在顶层而非 data 的端点，如注销提交的 executeAt） */
export async function requestBody<T extends BizResponse>(config: AxiosRequestConfig): Promise<T> {
  const response = await client.request<T>(config)
  const body = response.data
  if (body.code !== 200) {
    throw new BizError(body.code, body.msg || '请求失败，请稍后重试')
  }
  return body
}

/** 文件下载请求：返回 Blob，用于导出 Excel/PDF 等场景 */
export async function requestBlob(config: AxiosRequestConfig): Promise<Blob> {
  const response = await client.request({ ...config, responseType: 'blob' })
  return response.data as Blob
}
